(function () {

  class CartRewardsProgress extends HTMLElement {

    constructor() {
      super();
      this._busy = false;
      this._bound = false;
      this._refreshTimer = null;
    }

    connectedCallback() {
      if (this._bound) {
        this._refresh();
        return;
      }
      this._bound = true;
      this._bindBottleButton();
      this._refresh();
    }

    getRoot() {
      if (window.Shopify && Shopify.routes && Shopify.routes.root) {
        return Shopify.routes.root;
      }
      return '/';
    }

    async getCart() {
      const response = await fetch(this.getRoot() + 'cart.js?_=' + Date.now(), {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to load cart');
      return await response.json();
    }

    isEligible(item) {
      const title = (item.product_title || '').toLowerCase();
      return title.includes('detox mode') || title.includes('glow mode') || title.includes('focus mode');
    }

    isThreeBottle(item) {
      const title = (item.variant_title || '').toLowerCase();
      return title.includes('3 bottle') || title.includes('3-bottle') || title.includes('3 pack') || title.includes('3-pack') || title.includes('3pack') || title.includes('bundle');
    }

    isSingleBottle(item) {
      if (!this.isEligible(item)) return false;
      if (item.quantity !== 1) return false;
      if (this.isThreeBottle(item)) return false;
      return true;
    }

    getBottleCountFromVariant(item) {
      const title = (item.variant_title || '').toLowerCase();
      const match = title.match(/(\d+)\s*-?\s*bottle/i);
      if (!match) return null;
      return parseInt(match[1], 10);
    }

    isSingleBottleVariant(item) {
      return this.getBottleCountFromVariant(item) === 1;
    }

    async findThreeBottleVariant(sourceItem) {
      if (!sourceItem.handle) throw new Error('Product handle missing from cart item');

      const response = await fetch(
        this.getRoot() + 'products/' + encodeURIComponent(sourceItem.handle) + '.js?_=' + Date.now(),
        { method: 'GET', cache: 'no-store', headers: { 'Accept': 'application/json' } }
      );

      if (!response.ok) throw new Error('Unable to load product variants');

      const product = await response.json();
      if (!product || !Array.isArray(product.variants)) throw new Error('Product variants not found');

      const target = product.variants.find(variant => {
        const title = (variant.title || '').toLowerCase();
        return title.includes('3 bottle') || title.includes('3-bottle') || title.includes('3 pack') || title.includes('3-pack') || title.includes('3pack') || title.includes('bundle');
      });

      if (!target) throw new Error('3-bottle variant not found for ' + sourceItem.product_title);
      return target;
    }

    bottlesPerUnit(item) {
      const title = (item.variant_title || '').toLowerCase();
      const match = title.match(/(\d+)\s*-?\s*(bottle|pack)/);
      if (match) return parseInt(match[1], 10);
      if (title.includes('bundle')) return 3;
      return 1;
    }

    updateBottleProgress(cart) {
      let bottles = 0;
      cart.items.forEach(item => {
        if (!this.isEligible(item)) return;
        bottles += this.bottlesPerUnit(item) * item.quantity;
      });

      const target = parseInt(this.dataset.bottleTarget, 10) || 3;
      const percent = Math.min((bottles / target) * 100, 100);

      const fill = this.querySelector('.ai-cgp-bottle-fill');
      if (fill) fill.style.width = percent + '%';

      const fraction = this.querySelector('.ai-cgp-bottle-fraction');
      if (fraction) fraction.textContent = bottles + '/' + target;

      const subtitle = this.querySelector('[data-bottle-subtitle]');
      if (subtitle) {
        const remaining = Math.max(target - bottles, 0);
        subtitle.textContent = (this.dataset.bottleSubtitleTemplate || '').replace('[remaining]', remaining);
      }

      const locked = this.querySelector('[data-bottle-locked]');
      const unlocked = this.querySelector('[data-bottle-unlocked]');

      if (bottles >= target) {
        if (locked) locked.style.display = 'none';
        if (unlocked) unlocked.style.display = 'flex';
      } else {
        if (locked) locked.style.display = 'block';
        if (unlocked) unlocked.style.display = 'none';
      }
    }

    updateMainProgress(cart) {
      const total = Number(cart.total_price) || 0;
      const t1 = parseInt(this.dataset.t1, 10) || 0;
      const t2 = parseInt(this.dataset.t2, 10) || 0;
      const t3 = parseInt(this.dataset.t3, 10) || 1;

      const step1 = total >= t1;
      const step2 = total >= t2;
      const step3 = total >= t3;

      const fill = this.querySelector('.ai-cgp-main-fill');
      if (fill) fill.style.width = Math.min((total / t3) * 100, 100) + '%';

      const steps = this.querySelectorAll('.ai-cgp-step');
      if (steps[0]) steps[0].classList.toggle('complete', step1);
      if (steps[1]) steps[1].classList.toggle('complete', step2);
      if (steps[2]) steps[2].classList.toggle('complete', step3);

      const pill1 = this.querySelector('[data-pill="1"]');
      const pill2 = this.querySelector('[data-pill="2"]');
      const pill3 = this.querySelector('[data-pill="3"]');
      if (pill1) pill1.style.display = step1 ? 'flex' : 'none';
      if (pill2) pill2.style.display = step2 ? 'flex' : 'none';
      if (pill3) pill3.style.display = step3 ? 'flex' : 'none';
    }

    updateButton(cart) {
      const btn = this.querySelector('[data-bottle-btn]');
      if (!btn) return;

      const singleBottleItems = cart.items.filter(item => this.isSingleBottleVariant(item));

      if (singleBottleItems.length === 1) {
        btn.style.display = 'block';
        btn.disabled = false;
        btn.dataset.itemKey = singleBottleItems[0].key;
      } else {
        btn.style.display = 'none';
        btn.dataset.itemKey = '';
      }
    }

    async _refresh() {
      try {
        const cart = await this.getCart();
        this.updateBottleProgress(cart);
        this.updateMainProgress(cart);
        this.updateButton(cart);
      } catch (error) {
        console.error('CGP refresh error:', error);
      }
    }

    _bindBottleButton() {
      const btn = this.querySelector('[data-bottle-btn]');
      if (!btn) return;
      if (btn.dataset.cgpBound === 'true') return;
      btn.dataset.cgpBound = 'true';

      btn.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this._busy) return;

        this._busy = true;
        btn.disabled = true;
        const originalText = btn.textContent;
        btn.textContent = 'Updating...';

        try {
          const cart = await this.getCart();
          const singleBottleItems = cart.items.filter(item => this.isSingleBottleVariant(item));

          if (singleBottleItems.length !== 1) {
            await this._refresh();
            return;
          }

          const source = singleBottleItems[0];
          const targetVariant = await this.findThreeBottleVariant(source);

          if (!targetVariant || !targetVariant.id) {
            throw new Error('Target 3-bottle variant not found');
          }

          const updates = {};
          updates[source.key] = 0;
          updates[String(targetVariant.id)] = 1;

          const response = await fetch(this.getRoot() + 'cart/update.js?_=' + Date.now(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              updates: updates,
              sections: 'cart-drawer-section',
              sections_url: window.location.pathname
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error('Cart update failed: ' + errorText);
          }

          const newCart = await response.json();

          /*
           * Only swap the inner content of the drawer
           * (items list + summary), NOT the outer
           * <dialog>/<theme-drawer> wrapper — replacing
           * those would reset the drawer's open state
           * and make it disappear.
           */
          if (newCart.sections && newCart.sections['cart-drawer-section']) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(newCart.sections['cart-drawer-section'], 'text/html');

            const newInner = doc.querySelector('.cart-drawer__inner');
            const currentInner = document.querySelector('.cart-drawer__inner');

            if (newInner && currentInner) {
              currentInner.innerHTML = newInner.innerHTML;
            }
          }

          this.updateBottleProgress(newCart);
          this.updateMainProgress(newCart);
          this.updateButton(newCart);

          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: newCart } }));

          setTimeout(() => { this._refresh(); }, 150);

        } catch (error) {
          console.error('Bottle upgrade error:', error);
          await this._refresh();
        } finally {
          this._busy = false;
          btn.disabled = false;
          btn.textContent = originalText;
        }
      });
    }
  }

  if (!customElements.get('ai-cart-rewards-progress')) {
    customElements.define('ai-cart-rewards-progress', CartRewardsProgress);
  }

  document.addEventListener('cart:updated', () => {
    document.querySelectorAll('ai-cart-rewards-progress').forEach(component => {
      if (typeof component._refresh === 'function') {
        clearTimeout(component._refreshTimer);
        component._refreshTimer = setTimeout(() => { component._refresh(); }, 100);
      }
    });
  });

})();